import { OrderMode } from '@/enums/OrderEnums';
import { PostOrderDisplayMode } from '@/enums/PostOrderDisplayMode';
import { IBranch } from '@/interfaces/IBranch';

export const POST_ORDER_CUSTOM_MESSAGE_MAX_LENGTH = 4000;

export type TTranslateFn = (
  key: string,
  params?: Record<string, unknown>
) => string;

export type TBranchPostOrderSectionKey = 'table' | 'takeAway' | 'delivery';
export type TBranchPostOrderModeKey =
  | 'tablePostOrderDisplayMode'
  | 'takeAwayPostOrderDisplayMode'
  | 'deliveryPostOrderDisplayMode';
export type TBranchPostOrderCustomMessageKey =
  | 'tablePostOrderCustomMessageMd'
  | 'takeAwayPostOrderCustomMessageMd'
  | 'deliveryPostOrderCustomMessageMd';
export type TBranchPostOrderEnabledKey =
  | 'tableOrders'
  | 'takeAway'
  | 'delivery';

export interface IBranchPostOrderSectionConfig {
  key: TBranchPostOrderSectionKey;
  titleKey: string;
  subjectKey: string;
  disabledHintKey: string;
  enabledKey: TBranchPostOrderEnabledKey;
  modeKey: TBranchPostOrderModeKey;
  customMessageKey: TBranchPostOrderCustomMessageKey;
  allowedModes: readonly PostOrderDisplayMode[];
}

export interface IPostOrderModeOption {
  value: PostOrderDisplayMode;
  text: string;
  hint: string;
}

const COMMON_POST_ORDER_MODES = [
  PostOrderDisplayMode.Status,
  PostOrderDisplayMode.StatusCustom,
  PostOrderDisplayMode.Custom,
] as const satisfies readonly PostOrderDisplayMode[];

const TAKE_AWAY_POST_ORDER_MODES = [
  ...COMMON_POST_ORDER_MODES,
  PostOrderDisplayMode.CallByNumber,
  PostOrderDisplayMode.CallByName,
] as const satisfies readonly PostOrderDisplayMode[];

export const BRANCH_POST_ORDER_SECTION_KEYS = [
  'table',
  'takeAway',
  'delivery',
] as const satisfies readonly TBranchPostOrderSectionKey[];

const BRANCH_POST_ORDER_SECTION_CONFIG_MAP: Record<
  TBranchPostOrderSectionKey,
  IBranchPostOrderSectionConfig
> = {
  table: {
    key: 'table',
    titleKey: 'storeSettings.postOrder.table.title',
    subjectKey: 'storeSettings.postOrder.table.subject',
    disabledHintKey: 'storeSettings.postOrder.table.disabled',
    enabledKey: 'tableOrders',
    modeKey: 'tablePostOrderDisplayMode',
    customMessageKey: 'tablePostOrderCustomMessageMd',
    allowedModes: COMMON_POST_ORDER_MODES,
  },
  takeAway: {
    key: 'takeAway',
    titleKey: 'storeSettings.postOrder.takeAway.title',
    subjectKey: 'storeSettings.postOrder.takeAway.subject',
    disabledHintKey: 'storeSettings.postOrder.takeAway.disabled',
    enabledKey: 'takeAway',
    modeKey: 'takeAwayPostOrderDisplayMode',
    customMessageKey: 'takeAwayPostOrderCustomMessageMd',
    allowedModes: TAKE_AWAY_POST_ORDER_MODES,
  },
  delivery: {
    key: 'delivery',
    titleKey: 'storeSettings.postOrder.delivery.title',
    subjectKey: 'storeSettings.postOrder.delivery.subject',
    disabledHintKey: 'storeSettings.postOrder.delivery.disabled',
    enabledKey: 'delivery',
    modeKey: 'deliveryPostOrderDisplayMode',
    customMessageKey: 'deliveryPostOrderCustomMessageMd',
    allowedModes: COMMON_POST_ORDER_MODES,
  },
};

export function getBranchPostOrderSectionConfig(
  key: TBranchPostOrderSectionKey
): IBranchPostOrderSectionConfig {
  return BRANCH_POST_ORDER_SECTION_CONFIG_MAP[key];
}

function getCommonModeOptions(
  t: TTranslateFn,
  subject: string
): IPostOrderModeOption[] {
  return [
    {
      value: PostOrderDisplayMode.Status,
      text: t('storeSettings.postOrder.mode.status'),
      hint: t('storeSettings.postOrder.mode.status.hint'),
    },
    {
      value: PostOrderDisplayMode.StatusCustom,
      text: t('storeSettings.postOrder.mode.statusCustom'),
      hint: t('storeSettings.postOrder.mode.statusCustom.hint'),
    },
    {
      value: PostOrderDisplayMode.Custom,
      text: t('storeSettings.postOrder.mode.custom'),
      hint: t('storeSettings.postOrder.mode.custom.hint', { subject }),
    },
  ];
}

export function getBranchPostOrderModeOptions(
  key: TBranchPostOrderSectionKey,
  t: TTranslateFn
): IPostOrderModeOption[] {
  const config = getBranchPostOrderSectionConfig(key);
  const subject = t(config.subjectKey);
  const commonOptions = getCommonModeOptions(t, subject);

  if (key !== 'takeAway') return commonOptions;

  return [
    ...commonOptions,
    {
      value: PostOrderDisplayMode.CallByNumber,
      text: t('storeSettings.postOrder.mode.callByNumber'),
      hint: t('storeSettings.postOrder.mode.callByNumber.hint'),
    },
    {
      value: PostOrderDisplayMode.CallByName,
      text: t('storeSettings.postOrder.mode.callByName'),
      hint: t('storeSettings.postOrder.mode.callByName.hint'),
    },
  ];
}

export function normalizePostOrderCustomMessage(
  value: string | null | undefined
): string | null {
  if (typeof value !== 'string') return null;

  const normalized = value.replace(/\r\n?/g, '\n').trim();
  return normalized.length > 0 ? normalized : null;
}

export function getPostOrderCustomMessageLength(
  value: string | null | undefined
): number {
  return normalizePostOrderCustomMessage(value)?.length ?? 0;
}

export function hasPostOrderCustomDraft(
  value: string | null | undefined
): boolean {
  return getPostOrderCustomMessageLength(value) > 0;
}

export function modeRequiresCustomMessage(
  mode: PostOrderDisplayMode | null | undefined
): boolean {
  return (
    mode === PostOrderDisplayMode.Custom ||
    mode === PostOrderDisplayMode.StatusCustom
  );
}

export function shouldShowPostOrderCustomEditor(
  mode: PostOrderDisplayMode | null | undefined
): boolean {
  return modeRequiresCustomMessage(mode);
}

export function isBranchPostOrderSectionEnabled(
  branch: IBranch | null | undefined,
  key: TBranchPostOrderSectionKey
): boolean {
  if (!branch) return false;

  const config = getBranchPostOrderSectionConfig(key);
  return !!branch[config.enabledKey];
}

export function hasBranchPostOrderSavedState(
  branch: IBranch | null | undefined,
  key: TBranchPostOrderSectionKey
): boolean {
  if (!branch) return false;

  const config = getBranchPostOrderSectionConfig(key);
  const mode = branch[config.modeKey] ?? PostOrderDisplayMode.Status;
  return (
    mode !== PostOrderDisplayMode.Status ||
    hasPostOrderCustomDraft(branch[config.customMessageKey])
  );
}

export function shouldShowBranchPostOrderSection(
  branch: IBranch | null | undefined,
  key: TBranchPostOrderSectionKey
): boolean {
  return (
    isBranchPostOrderSectionEnabled(branch, key) ||
    hasBranchPostOrderSavedState(branch, key)
  );
}

export function getBranchPostOrderStatusHint(
  branch: IBranch | null | undefined,
  key: TBranchPostOrderSectionKey,
  t: TTranslateFn
): string | null {
  if (!branch) return null;

  const config = getBranchPostOrderSectionConfig(key);

  if (!branch.allowOrders) {
    return t('storeSettings.postOrder.hint.ordersDisabled');
  }

  if (branch.ordersMode !== OrderMode.Native) {
    return t('storeSettings.postOrder.hint.whatsappMode');
  }

  if (!branch[config.enabledKey]) {
    return t('storeSettings.postOrder.hint.orderTypeDisabled', {
      orderType: t(config.disabledHintKey),
    });
  }

  return null;
}
